// Тосты живут поверх окон и панелей (ux.md, раздел 2): «Отменить» и «Повторить» в тосте нажимаются,
// даже когда открыто окно или панель на весь экран. Нажатие в тосте — не «клик мимо» окна:
// окно от него не закрывается.
//
// Radix Dialog решает, был ли «клик мимо», уже после click (deferPointerDownOutside), а кнопка
// действия к этому времени закрыла свой тост: элемент нажатия вынут из страницы вместе с тостом,
// и области тостов среди его предков нет. Поэтому тост узнаётся и по самому тосту (.t-toast).

/** Событие «снаружи» слоя Radix пришло из области тостов или из тоста, который уже закрылся. */
export function isToastTarget(event: Event): boolean {
  const target = event.target;
  return target instanceof Element && target.closest('.kp-toasts, .t-toast') !== null;
}

/** Обработчик «снаружи» для окон: нажатие в тосте окно не закрывает. */
export function keepOpenForToasts(event: Event): void {
  if (isToastTarget(event)) event.preventDefault();
}

// Esc не теряется (044, MOT-07, MOT-16, research Р4). DismissableLayer Radix слушает Esc только
// у верхнего слоя и отменяет событие. Открытая подсказка — верхний слой: окно под ней Esc
// не получает. Слой на выходе ещё смонтирован — он же забирает и второй Esc. Такой слой снимается
// сразу, без выхода, и Esc посылается заново тому, кто теперь в фокусе: следующему слою или
// странице. Повторно посланный Esc дальше не передаётся.
//
// Уходящая подсказка — не слой для Esc (044, T043, MOT-16): Tab с кнопки на кнопку оставляет
// подсказку прежней кнопки гаснуть 120 мс, и её DismissableLayer забрал бы и повторно посланный Esc
// (календарь оставался открытым). Перед повторной отправкой уходящие подсказки тоже снимаются.
//
// Esc во время выхода закрывает следующий слой не позже конца выхода (044, T045, MOT-07 MUST).
// Браузер шлёт animationend доигранного выхода только в следующем кадре, а React снимает слой ещё
// позже — под нагрузкой панель под окном начинала уходить через 230 мс после второго Esc. Уходящий
// слой снимается сразу: выход доигрывается, Presence получает animationend тут же (flushSync),
// и Esc уходит следующему слою в том же нажатии. Открытый слой (подсказка при первом Esc) сначала
// закрывает Radix — его снимают после этого, на следующем шаге.
import { flushSync } from 'react-dom';

/** Дольше слой не ждём: выход длиннее 240 мс не бывает. */
const RELAY_WAIT_MS = 300;
/** Шаг проверки, если слой не снялся сразу (выхода нет или его снимает не Presence). */
const RELAY_STEP_MS = 16;

/** Подсказка, которая уходит: её снимают вместе со слоем перед повторной отправкой Esc. */
const LEAVING_TIPS = '.kp-tip[data-state="closed"]';

/** Идёт повторная отправка Esc: этот Esc второй раз не передаётся. */
let relaying = false;

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/** Доиграть выход узла сразу: Presence Radix снимает его по animationend. */
function finishAnimations(node: HTMLElement): void {
  if (typeof node.getAnimations !== 'function') return;
  const ended: string[] = [];
  for (const animation of node.getAnimations()) {
    try {
      animation.finish();
    } catch {
      // Бесконечная анимация не доигрывается; у слоёв таких нет.
      continue;
    }
    const name = (animation as Partial<CSSAnimation>).animationName;
    if (typeof name === 'string' && name !== '') ended.push(name);
  }
  if (ended.length === 0 || !node.isConnected) return;
  // animationend браузер пришлёт только в следующем кадре: Presence Radix и usePresence получают
  // его сразу, а React снимает слой в этом же вызове — вместе со слушателем Esc (T045).
  flushSync(() => {
    for (const name of ended) node.dispatchEvent(animationEnd(name));
  });
}

function animationEnd(animationName: string): Event {
  if (typeof AnimationEvent === 'function') {
    return new AnimationEvent('animationend', { animationName, bubbles: true });
  }
  const event = new Event('animationend', { bubbles: true });
  Object.defineProperty(event, 'animationName', { value: animationName });
  return event;
}

/** Слой уходит: Radix и usePresence ставят data-state="closed", тосты — .is-leaving. */
function isLeaving(node: HTMLElement): boolean {
  return node.getAttribute('data-state') === 'closed' || node.classList.contains('is-leaving');
}

function sendEscape(): void {
  const target = document.activeElement ?? document.body;
  relaying = true;
  try {
    target.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Escape',
        code: 'Escape',
        bubbles: true,
        cancelable: true,
        composed: true,
      }),
    );
  } finally {
    relaying = false;
  }
}

/**
 * Esc ушёл в слой, который сам его не обрабатывает (подсказка) или уже уходит: слой снимается
 * без выхода, и Esc посылается заново элементу в фокусе, когда слоя уже нет (не дольше 300 мс).
 * event — нажатие, которое пришло в слой: дальше по странице оно не идёт, чтобы страница не
 * получила Esc дважды.
 */
export function relayEscape(node: HTMLElement | null, event?: Event): void {
  if (relaying) return;
  event?.stopPropagation();
  const started = now();
  const waiting = () =>
    [node, ...Array.from(document.querySelectorAll<HTMLElement>(LEAVING_TIPS))].filter(
      (layer): layer is HTMLElement => layer?.isConnected === true,
    );
  const step = () => {
    for (const layer of waiting()) finishAnimations(layer);
    if (waiting().length > 0 && now() - started < RELAY_WAIT_MS) {
      window.setTimeout(step, RELAY_STEP_MS);
      return;
    }
    sendEscape();
  };
  // Слой уже уходит (второй Esc) — снять его и передать Esc сразу, в этом же нажатии. Открытый
  // слой (подсказка) Radix закроет после этого обработчика — тогда и следующий шаг.
  if (node?.isConnected && isLeaving(node)) step();
  else window.setTimeout(step, 0);
}
